import { useEffect, useState } from 'react'
import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined'
import type { DummyWorkOrder, WoPriority } from '../dummy'
import { PRIORITY_META } from '../dummy'
import { AssigneeAvatars } from './chips'
import { useSelectWorkOrder, useWorkOrderRowMenu } from '../WorkOrdersPage'
import { SpotlightCard } from './SpotlightCard'
import { summarizeSchedule, nextFireMoment, formatNextFire } from '../scheduleSummary'
import { useOrgSettings } from '../../../hooks/useOrgSettings'

const dayMs = 24 * 60 * 60 * 1000

export function WorkOrderCard({
  wo, compact = false,
}: {
  wo: DummyWorkOrder
  compact?: boolean
}) {
  // Compare calendar dates, not exact timestamps — due_date has no
  // time component, so diffing it straight against `new Date()` (which
  // does) made anything due "today" look overdue by a fraction of a
  // day for the rest of the day, e.g. 2pm vs a midnight due_date
  // rounds to -1 day. Normalizing both sides to midnight fixes that;
  // this is the same mismatch that made the Overview's Overdue count
  // (which already compares date-to-date server-side) read 0 while
  // individual cards due today showed an OVERDUE badge.
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueDateStart = new Date(wo.dueDate.getFullYear(), wo.dueDate.getMonth(), wo.dueDate.getDate())
  const daysUntil = Math.round((dueDateStart.getTime() - todayStart.getTime()) / dayMs)
  const overdue = daysUntil < 0 && wo.status !== 'closed' && wo.status !== 'resolved'
  const dueLabel =
    daysUntil === 0  ? 'Today'              :
    daysUntil === 1  ? 'Tomorrow'           :
    daysUntil === -1 ? 'Yesterday'          :
    daysUntil < 0    ? `${-daysUntil}d overdue` :
                       `in ${daysUntil}d`

  const select = useSelectWorkOrder()
  const openRowMenu = useWorkOrderRowMenu()
  const { formatDate, formatDateShort } = useOrgSettings()
  // wo.dueDate always has a value (adaptWorkOrder falls back to "now"
  // when there's no real due_date) — check the raw field so a WO with
  // no due date genuinely shows nothing here instead of a misleading date.
  const hasRealDueDate = !!wo._raw.due_date
  const startDateLabel = wo.startDate ? formatDateShort(wo.startDate) : null
  const dueDateLabel = hasRealDueDate ? formatDateShort(wo.dueDate) : null
  const pulse = overdue && wo.priority === 'critical'

  // Calendar-driven recurring template: render a "Template" badge in the
  // top row + a sub-line summarising the schedule and when it next fires.
  // The data fields are exposed on the WO row (is_schedule_template +
  // schedule_config + last_spawned_at) so no extra query is needed.
  const isTemplate = !!wo._raw.is_schedule_template
  const isPreview = !!wo._raw.is_preview
  const scheduleSummary = isTemplate ? summarizeSchedule(wo._raw.schedule_config) : null
  const nextFire = isTemplate ? nextFireMoment(wo._raw.schedule_config) : null
  const nextFireLabel = nextFire ? formatNextFire(nextFire) : null
  const previewFireLabel = (() => {
    if (!isPreview) return null
    const iso = wo._raw.scheduled_for
    if (!iso) return null
    try {
      return formatDate(new Date(iso), true)
    } catch { return null }
  })()

  return (
    <SpotlightCard
      pulse={pulse}
      onClick={() => select(wo)}
      onContextMenu={(e) => openRowMenu(e, wo)}
      sx={{
        p: compact ? 1.5 : 2,
        ...(isPreview && {
          opacity: 0.85,
          borderStyle: 'dashed',
          borderColor: '#CBD5E1',
        }),
      }}
    >
      {/* Top row — priority dot, ID, spacer, optional overdue badge */}
      <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mb: compact ? 0.75 : 1 }}>
        <PriorityDot priority={wo.priority} />
        <Typography
          sx={{
            fontSize: 11, fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.02em',
            fontFamily: 'ui-monospace, monospace',
          }}
          noWrap
        >
          {wo._raw.wo_code || (wo.id.length > 12 ? `${wo._raw.kind === 'task' ? 'TSK' : 'WO'}-${wo.id.slice(0, 6).toUpperCase()}` : wo.id)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {isTemplate && (
          <Chip
            label="TEMPLATE"
            size="small"
            sx={{
              height: 18, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: '#EFF6FF', color: '#0EA5E9',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {wo._raw.kind === 'task' && (
          <Chip
            label="TASK"
            size="small"
            sx={{
              height: 18, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: '#EFF6FF', color: '#1D4ED8',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {/* For tasks, surface the scheduled / unscheduled split as a
            second chip so the user can tell the two flavors apart at
            a glance from the kanban without opening the row. A task
            is "scheduled" when it has a future scheduled_for, is a
            recurring template, or is sitting in `'scheduled'` status
            (pre-promoted by the scheduler tick). Anything else on a
            task is "unscheduled" — created now, status='open'. */}
        {wo._raw.kind === 'task' && !isPreview && (
          (wo._raw.status === 'scheduled'
           || wo._raw.is_schedule_template
           || !!wo._raw.scheduled_for)
            ? (
              <Chip
                label="SCHEDULED"
                size="small"
                sx={{
                  height: 18, fontSize: 9.5, fontWeight: 700,
                  letterSpacing: '0.04em',
                  bgcolor: '#F0FDFA', color: '#0F766E',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ) : (
              <Chip
                label="UNSCHEDULED"
                size="small"
                sx={{
                  height: 18, fontSize: 9.5, fontWeight: 700,
                  letterSpacing: '0.04em',
                  bgcolor: '#F5F3FF', color: '#6D28D9',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )
        )}
        {isPreview && (
          <Chip
            label="UPCOMING"
            size="small"
            sx={{
              height: 18, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: '#FFFBEB', color: '#B45309',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {overdue && (
          <Chip
            label="OVERDUE"
            size="small"
            sx={{
              height: 18, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: '#FEF2F2', color: '#DC2626',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {wo._raw.pending_my_approval && (
          <Tooltip title="Waiting on your approval">
            <Chip
              label="PENDING APPROVAL"
              size="small"
              sx={{
                height: 18, fontSize: 9.5, fontWeight: 700,
                letterSpacing: '0.04em',
                bgcolor: '#FFFBEB', color: '#B45309',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Tooltip>
        )}
      </Stack>

      {/* Title */}
      <Typography
        sx={{
          fontSize: compact ? 13.5 : 14.5,
          fontWeight: 600,
          letterSpacing: '-0.005em',
          lineHeight: 1.35,
          mb: compact ? 0.75 : 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {wo.title}
      </Typography>

      {(startDateLabel || dueDateLabel) && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }} noWrap>
            {startDateLabel && dueDateLabel
              ? `${startDateLabel} → ${dueDateLabel}`
              : startDateLabel
                ? `Start ${startDateLabel}`
                : `Due ${dueDateLabel}`}
          </Typography>
        </Stack>
      )}

      {isPreview && previewFireLabel && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <ScheduleOutlinedIcon sx={{ fontSize: 13, color: '#B45309' }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#B45309' }} noWrap>
            Fires {previewFireLabel}
          </Typography>
        </Stack>
      )}

      {/* Schedule summary — only shown on recurring templates so admins can
          tell at a glance which row is the rule vs. which is a pending
          one-off scheduled WO. */}
      {isTemplate && (scheduleSummary || nextFireLabel) && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <EventRepeatOutlinedIcon sx={{ fontSize: 13, color: '#0EA5E9' }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#0EA5E9' }} noWrap>
            {scheduleSummary}
            {scheduleSummary && nextFireLabel && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {` · Next: ${nextFireLabel}`}
              </Box>
            )}
            {!scheduleSummary && nextFireLabel && `Next: ${nextFireLabel}`}
          </Typography>
        </Stack>
      )}

      {/* SLA timer badge */}
      <SlaTimerBadge wo={wo} />

      {/* Secondary lines — asset + location (non-compact only) */}
      {!compact && wo.asset && (
        <Tooltip title={wo.asset.name} arrow enterDelay={400}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5, minWidth: 0 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {wo.asset.name}
            </Typography>
          </Stack>
        </Tooltip>
      )}
      {!compact && (
        <Tooltip title={wo.location} arrow enterDelay={400}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5, minWidth: 0 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {wo.location}
            </Typography>
          </Stack>
        </Tooltip>
      )}

      {/* Progress (neutral) */}
      {typeof wo.progress === 'number' && (
        <Box sx={{ mb: 1.25 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
              Progress
            </Typography>
            <Typography
              sx={{
                fontSize: 11, fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: 'text.secondary',
              }}
            >
              {wo.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={wo.progress}
            sx={{
              height: 3,
              borderRadius: 99,
              bgcolor: (t) =>
                t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'text.primary',
                opacity: 0.4,
                borderRadius: 99,
              },
            }}
          />
        </Box>
      )}

      {/* Footer */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <AssigneeAvatars assignees={wo.assignees} max={3} />

        <Stack direction="row" alignItems="center" spacing={1.25}>
          {wo.commentCount > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
                {wo.commentCount}
              </Typography>
            </Stack>
          )}
          {wo.attachmentCount > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <AttachFileOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
                {wo.attachmentCount}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ScheduleOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: overdue ? 600 : 500,
                color: 'text.secondary',
              }}
            >
              {dueLabel}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </SpotlightCard>
  )
}

// ─── Priority dot — a single 6px circle, the only colour on the card ────────

function PriorityDot({ priority }: { priority: WoPriority }) {
  const m = PRIORITY_META[priority] ?? { label: 'Medium', color: '#D97706', bg: '#FFFBEB' }
  const color = priority === 'low' || priority === 'medium'
    ? 'rgba(107,114,128,0.7)'
    : m.color
  return (
    <Box
      aria-label={`Priority ${m.label}`}
      sx={{
        width: 6,
        height: 6,
        borderRadius: 99,
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  )
}

function SlaTimerBadge({ wo }: { wo: DummyWorkOrder }) {
  const raw = wo._raw
  const hasSla = !!raw.sla_policy_id && raw.status !== 'resolved'

  // Tick every 30s to keep the countdown live
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!hasSla) return
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [hasSla])

  if (!hasSla) return null

  const now = Date.now()
  const responseDue = raw.sla_response_due ? new Date(raw.sla_response_due).getTime() : null
  const resolutionDue = raw.sla_resolution_due ? new Date(raw.sla_resolution_due).getTime() : null
  const respondedAt = raw.sla_responded_at ? new Date(raw.sla_responded_at).getTime() : null

  let deadline: number | null = null
  let label = ''

  if (!respondedAt && responseDue) {
    deadline = responseDue
    const diff = responseDue - now
    label = diff <= 0 ? `Response breached ${fmtDuration(Math.abs(diff))} ago` : `Respond in ${fmtDuration(diff)}`
  } else if (resolutionDue && raw.status !== 'closed') {
    deadline = resolutionDue
    const diff = resolutionDue - now
    label = diff <= 0 ? `Resolution breached ${fmtDuration(Math.abs(diff))} ago` : `Resolve in ${fmtDuration(diff)}`
  }

  if (!label) return null

  const breached = !!raw.sla_breached || (deadline ? deadline - now <= 0 : false)
  const pct = deadline ? Math.min(100, Math.max(0, ((now - new Date(raw.created_at).getTime()) / (deadline - new Date(raw.created_at).getTime())) * 100)) : 0
  const color = breached ? '#DC2626' : pct > 80 ? '#D97706' : '#059669'

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
      <TimerOutlinedIcon sx={{ fontSize: 13, color }} />
      <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{label}</Typography>
      <Box sx={{ flex: 1, height: 3, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden', ml: 0.5 }}>
        <Box sx={{ height: '100%', borderRadius: 99, width: `${pct}%`, bgcolor: color, transition: 'width 0.5s ease' }} />
      </Box>
    </Stack>
  )
}

function fmtDuration(ms: number): string {
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h`
}
